import React, { useEffect, useMemo, useState } from 'react';
import { SET_THE_REALM, SET_THE_TOKEN } from "../app/start/actions";
import connect from '../shared/connectWithOnLoad';

// ─── Gruppierte Kontexte ────────────────────────────────────────────────────

/** Token und Realm (Auth) */
export const TokenContext = React.createContext('');
export const RealmContext = React.createContext('');

/** Farb-Thema (hell/dunkel) */
export const SiteColorContext = React.createContext('');

/** Suchfeld */
export const SearchValueContext = React.createContext('');

/**
 * Katalog / Tree-Daten
 * { items, setItems, clicked, setClicked, isLoaded, setIsLoaded,
 *   selected, setSelected, selectedItem, setSelectedItem,
 *   parent, setParent, catalogParentId, setCatalogParentId }
 */
export const TreeContext = React.createContext(null);

/**
 * BIM-Profile
 * { profiles, setProfiles, clicked, setClicked, isLoaded, setIsLoaded,
 *   selected, setSelected, selectedData, setSelectedData }
 */
export const ProfileContext = React.createContext(null);

/**
 * Projekte
 * { projects, setProjects, userProjects, setUserProjects,
 *   clicked, setClicked, isLoaded, setIsLoaded,
 *   userProjectIsLoaded, setUserProjectIsLoaded,
 *   selected, setSelected }
 */
export const ProjectContext = React.createContext(null);

/**
 * Daten-Objekte und -Komponenten
 * { objects, setObjects, components, setComponents,
 *   objectsClicked, setObjectsClicked, componentsClicked, setComponentsClicked,
 *   isLoaded, setIsLoaded, dialogOpen, setDialogOpen }
 */
export const DataContext = React.createContext(null);

/**
 * Bauteil-Attribute
 * { selected, setSelected, desc, setDesc, id, setId,
 *   ifcType, setIfcType, isSelected, setIsSelected }
 */
export const AttributeContext = React.createContext(null);

/**
 * Ausgewähltes Bauteil
 * { isSelected, setIsSelected, name, setName, code, setCode, desc, setDesc }
 */
export const ComponentContext = React.createContext(null);

/**
 * Dialog-Zustände (alle modalen Dialoge)
 * { open, setOpen, subOpen, setSubOpen, subConfirmOpen, setSubConfirmOpen,
 *   updateCompOpen, setUpdateCompOpen, deleteCompOpen, setDeleteCompOpen,
 *   updateAttrOpen, setUpdateAttrOpen, deleteAttrOpen, setDeleteAttrOpen,
 *   attrOpen, setAttrOpen, bimOpen, setBimOpen }
 */
export const DialogContext = React.createContext(null);

// ─── Store-Komponente ────────────────────────────────────────────────────────

const Store = ({ children, theRealm, setTheRealm, keycloakToken, setKeycloakToken }) => {
    // Auth
    const [token, setToken] = useState('Kein Token generiert!');
    const [realm, setRealm] = useState(theRealm);

    // UI
    const [SiteColor, setSiteColor] = useState(true);
    const [SearchValue, setSearchValue] = useState('');

    // Tree / Katalog
    const [TreeItemsState, setTreeItemsState] = useState([]);
    const [TreeClicked, setTreeClicked] = useState(false);
    const [TreeIsLoaded, setTreeIsLoaded] = useState(false);
    const [TreeSelected, setTreeSelected] = useState('');
    const [TreeSelectedItem, setTreeSelectedItem] = useState([]);
    const [TreeParent, setTreeParent] = useState([]);
    const [CatalogParentId, setCatalogParentId] = useState([]);

    // Profile
    const [BimProf, setBimProf] = useState([]);
    const [ProfilClicked, setProfilClicked] = useState(false);
    const [ProfilIsLoaded, setProfilIsLoaded] = useState(false);
    const [BimProfileSelected, setBimProfileSelected] = useState('');
    const [BimProfileSelectedData, setBimProfileSelectedData] = useState([]);

    // Projekte
    const [BimProjects, setBimProjects] = useState([]);
    const [BimUserProjects, setBimUserProjects] = useState([]);
    const [BimProjectSelected, setBimProjectSelected] = useState([]);
    const [ProjectClicked, setProjectClicked] = useState(false);
    const [ProjectIsLoaded, setProjectIsLoaded] = useState(false);
    const [UserProjectIsLoaded, setUserProjectIsLoaded] = useState(false);

    // Daten-Objekte / Komponenten
    const [Dataobjects, setDataobjects] = useState([]);
    const [Datacomponents, setDatacomponents] = useState([]);
    const [DataobjectsClicked, setDataobjectsClicked] = useState(false);
    const [DatacomponentsClicked, setDatacomponentsClicked] = useState(false);
    const [DataobjectsIsLoaded, setDataobjectsIsLoaded] = useState(false);
    const [DataobjectsDialogOpen, setDataobjectsDialogOpen] = useState(false);

    // Attribute
    const [AttrSelected, setAttrSelected] = useState('');
    const [AttrSelect_Desc, setAttrSelect_Desc] = useState('');
    const [AttrSelect_Id, setAttrSelect_Id] = useState('');
    const [AttrSelect_IfcType, setAttrSelect_IfcType] = useState('');
    const [AttrIsSelected, setAttrIsSelected] = useState(false);

    // Bauteile
    const [CompIsSelected, setCompIsSelected] = useState(false);
    const [BTName, setBTName] = useState('');
    const [BTCode, setBTCode] = useState('');
    const [BTDesc, setBTDesc] = useState('');

    // Dialoge
    const [DialogOpen, setDialogOpen] = useState(false);
    const [SubDialogOpen, setSubDialogOpen] = useState(false);
    const [SubDialogConfirmOpen, setSubDialogConfirmOpen] = useState(false);
    const [UpdateCompDialogOpen, setUpdateCompDialogOpen] = useState(false);
    const [DeleteCompDialogOpen, setDeleteCompDialogOpen] = useState(false);
    const [UpdateAttrDialogOpen, setUpdateAttrDialogOpen] = useState(false);
    const [DeleteAttrDialogOpen, setDeleteAttrDialogOpen] = useState(false);
    const [AttrDialogOpen, setAttrDialogOpen] = useState(false);
    const [BIMDialogOpen, setBIMDialogOpen] = useState(false);

    // Session-Restore
    const myToken = sessionStorage.getItem('kctoken');
    if (myToken && myToken !== 'undefined') {
        keycloakToken = myToken;
    }
    if (theRealm === '' && sessionStorage.getItem('realm_stuff')) {
        theRealm = sessionStorage.getItem('realm_stuff');
        setTheRealm(theRealm);
    }

    useEffect(() => {
        setToken(keycloakToken);
        setRealm(theRealm);
        setKeycloakToken(keycloakToken);
    }, [keycloakToken, theRealm, setKeycloakToken]);

    // Gruppierte Context-Werte (mit useMemo für Performance)
    const treeValue = useMemo(() => ({
        items: TreeItemsState, setItems: setTreeItemsState,
        clicked: TreeClicked, setClicked: setTreeClicked,
        isLoaded: TreeIsLoaded, setIsLoaded: setTreeIsLoaded,
        selected: TreeSelected, setSelected: setTreeSelected,
        selectedItem: TreeSelectedItem, setSelectedItem: setTreeSelectedItem,
        parent: TreeParent, setParent: setTreeParent,
        catalogParentId: CatalogParentId, setCatalogParentId,
    }), [TreeItemsState, TreeClicked, TreeIsLoaded, TreeSelected, TreeSelectedItem, TreeParent, CatalogParentId]);

    const profileValue = useMemo(() => ({
        profiles: BimProf, setProfiles: setBimProf,
        clicked: ProfilClicked, setClicked: setProfilClicked,
        isLoaded: ProfilIsLoaded, setIsLoaded: setProfilIsLoaded,
        selected: BimProfileSelected, setSelected: setBimProfileSelected,
        selectedData: BimProfileSelectedData, setSelectedData: setBimProfileSelectedData,
    }), [BimProf, ProfilClicked, ProfilIsLoaded, BimProfileSelected, BimProfileSelectedData]);

    const projectValue = useMemo(() => ({
        projects: BimProjects, setProjects: setBimProjects,
        userProjects: BimUserProjects, setUserProjects: setBimUserProjects,
        clicked: ProjectClicked, setClicked: setProjectClicked,
        isLoaded: ProjectIsLoaded, setIsLoaded: setProjectIsLoaded,
        userProjectIsLoaded: UserProjectIsLoaded, setUserProjectIsLoaded,
        selected: BimProjectSelected, setSelected: setBimProjectSelected,
    }), [BimProjects, BimUserProjects, ProjectClicked, ProjectIsLoaded, UserProjectIsLoaded, BimProjectSelected]);

    const dataValue = useMemo(() => ({
        objects: Dataobjects, setObjects: setDataobjects,
        components: Datacomponents, setComponents: setDatacomponents,
        objectsClicked: DataobjectsClicked, setObjectsClicked: setDataobjectsClicked,
        componentsClicked: DatacomponentsClicked, setComponentsClicked: setDatacomponentsClicked,
        isLoaded: DataobjectsIsLoaded, setIsLoaded: setDataobjectsIsLoaded,
        dialogOpen: DataobjectsDialogOpen, setDialogOpen: setDataobjectsDialogOpen,
    }), [Dataobjects, Datacomponents, DataobjectsClicked, DatacomponentsClicked, DataobjectsIsLoaded, DataobjectsDialogOpen]);

    const attributeValue = useMemo(() => ({
        selected: AttrSelected, setSelected: setAttrSelected,
        desc: AttrSelect_Desc, setDesc: setAttrSelect_Desc,
        id: AttrSelect_Id, setId: setAttrSelect_Id,
        ifcType: AttrSelect_IfcType, setIfcType: setAttrSelect_IfcType,
        isSelected: AttrIsSelected, setIsSelected: setAttrIsSelected,
    }), [AttrSelected, AttrSelect_Desc, AttrSelect_Id, AttrSelect_IfcType, AttrIsSelected]);

    const componentValue = useMemo(() => ({
        isSelected: CompIsSelected, setIsSelected: setCompIsSelected,
        name: BTName, setName: setBTName,
        code: BTCode, setCode: setBTCode,
        desc: BTDesc, setDesc: setBTDesc,
    }), [CompIsSelected, BTName, BTCode, BTDesc]);

    const dialogValue = useMemo(() => ({
        open: DialogOpen, setOpen: setDialogOpen,
        subOpen: SubDialogOpen, setSubOpen: setSubDialogOpen,
        subConfirmOpen: SubDialogConfirmOpen, setSubConfirmOpen: setSubDialogConfirmOpen,
        updateCompOpen: UpdateCompDialogOpen, setUpdateCompOpen: setUpdateCompDialogOpen,
        deleteCompOpen: DeleteCompDialogOpen, setDeleteCompOpen: setDeleteCompDialogOpen,
        updateAttrOpen: UpdateAttrDialogOpen, setUpdateAttrOpen: setUpdateAttrDialogOpen,
        deleteAttrOpen: DeleteAttrDialogOpen, setDeleteAttrOpen: setDeleteAttrDialogOpen,
        attrOpen: AttrDialogOpen, setAttrOpen: setAttrDialogOpen,
        bimOpen: BIMDialogOpen, setBimOpen: setBIMDialogOpen,
    }), [DialogOpen, SubDialogOpen, SubDialogConfirmOpen, UpdateCompDialogOpen, DeleteCompDialogOpen, UpdateAttrDialogOpen, DeleteAttrDialogOpen, AttrDialogOpen, BIMDialogOpen]);

    return (
        <TokenContext.Provider value={[token, setToken]}>
        <RealmContext.Provider value={[realm, setRealm]}>
        <SiteColorContext.Provider value={[SiteColor, setSiteColor]}>
        <SearchValueContext.Provider value={[SearchValue, setSearchValue]}>
        <TreeContext.Provider value={treeValue}>
        <ProfileContext.Provider value={profileValue}>
        <ProjectContext.Provider value={projectValue}>
        <DataContext.Provider value={dataValue}>
        <AttributeContext.Provider value={attributeValue}>
        <ComponentContext.Provider value={componentValue}>
        <DialogContext.Provider value={dialogValue}>
            {children}
        </DialogContext.Provider>
        </ComponentContext.Provider>
        </AttributeContext.Provider>
        </DataContext.Provider>
        </ProjectContext.Provider>
        </ProfileContext.Provider>
        </TreeContext.Provider>
        </SearchValueContext.Provider>
        </SiteColorContext.Provider>
        </RealmContext.Provider>
        </TokenContext.Provider>
    );
};

function mapStateToProps({ theRealm, keycloakToken }) {
    return { theRealm, keycloakToken };
}

function mapDispatchToProps(dispatch) {
    return {
        setTheRealm(theRealm) {
            dispatch({ type: SET_THE_REALM, action: theRealm });
        },
        setKeycloakToken(keycloakToken) {
            dispatch({ type: SET_THE_TOKEN, action: keycloakToken });
        },
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(Store);
